import React from 'react';
import PropTypes from 'prop-types';

import {Menu, MenuItem, Pill, Stack, Typography} from 'views/components';
import {ArrowDropDown} from 'views/icons';

const PillDropdown = (props) => {
  const {
    backgroundColor,
    borderColor,
    label,
    menuLabel,
    onOpenChange,
    onOptionSelect,
    options,
    selectedValue,
    textColor,
  } = props;

  const [anchorEl, setAnchorEl] = React.useState(null);

  const isMenuOpen = Boolean(anchorEl);

  React.useEffect(() => {
    onOpenChange?.(isMenuOpen);
  }, [isMenuOpen, onOpenChange]);

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    closeMenu();
  };

  return (
    <>
      <Stack
        sx={{
          display: 'inline-flex',
          position: 'relative',
          alignItems: 'center',
        }}
      >
        <Pill
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          label={label}
          onClick={handleTriggerClick}
          aria-expanded={isMenuOpen}
          aria-haspopup='menu'
          role='button'
          sx={{
            'minWidth': 0,
            'gap': 0,
            'borderRadius': '20px',
            'cursor': 'pointer',
            'transition': 'box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
            '&:hover': {
              boxShadow: '0px 0px 0px 3px rgba(218, 121, 85, 0.12)',
            },
          }}
          tabIndex={0}
          textColor={textColor}
        />
        {
          isMenuOpen &&
          <ArrowDropDown
            sx={{
              position: 'absolute',
              right: '-12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '8px',
              height: '8px',
              color: textColor,
              pointerEvents: 'none',
            }}
          />
        }
      </Stack>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        MenuListProps={{disablePadding: true}}
        onClose={handleMenuClose}
        open={isMenuOpen}
        slotProps={{
          paper: {
            sx: {
              mt: '8px',
              border: '1px solid #D9D8D5',
              boxShadow: '0px 4px 12.1px rgba(0, 0, 0, 0.05)',
              borderRadius: '4px',
            },
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transitionDuration={0}
      >
        <Stack
          sx={{
            boxSizing: 'border-box',
            width: '230px',
            p: '14px 25px 25px 14px',
            gap: '10px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '11px',
              lineHeight: '14px',
              color: '#A8A49E',
              textTransform: 'uppercase',
            }}
          >
            {menuLabel}
          </Typography>
          <Stack sx={{gap: '12px'}}>
            {
              options.map(([optionKey, optionValue]) => {
                const isSelected = selectedValue === optionKey;

                return (
                  <MenuItem
                    key={optionKey}
                    onClick={() => {
                      closeMenu();
                      onOptionSelect(optionKey);
                    }}
                    selected={isSelected}
                    sx={{
                      'm': 0,
                      'px': '6px',
                      'py': '2px',
                      'minHeight': '22px',
                      'borderRadius': '4px',
                      'backgroundColor': 'transparent',
                      '&:hover': {
                        'backgroundColor': '#F7F4EF',
                      },
                      '&.Mui-selected': {
                        'backgroundColor': '#F7F4EF',
                      },
                      '&.Mui-selected:hover': {
                        'backgroundColor': '#EEE8DF',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: isSelected ? 500 : 400,
                        fontSize: '14px',
                        lineHeight: '18px',
                        color: isSelected ? '#DA7955' : '#000000',
                      }}
                    >
                      {optionValue.Title}
                    </Typography>
                  </MenuItem>
                );
              })
            }
          </Stack>
        </Stack>
      </Menu>
    </>
  );
};

PillDropdown.propTypes = {
  backgroundColor: PropTypes.string.isRequired,
  borderColor: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  menuLabel: PropTypes.string.isRequired,
  onOpenChange: PropTypes.func,
  onOptionSelect: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  selectedValue: PropTypes.string,
  textColor: PropTypes.string.isRequired,
};

export default PillDropdown;
